import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useMentionableEntities } from '@cobliteam/hooks/src/useSearchStore';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import Feedback from '@cobliteam/ui-business/components/Feedback';
import type { ChatStatus, UIMessage } from 'ai';
import { nanoid } from 'nanoid';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAIAssistant } from '../store';
import AIEntityMentionsInput, { type AIEntityMentionsInputHandle, MENTIONS_DISABLED } from './AIEntityMentionsInput';
import { ALL_MENTIONABLE_ENTITY_TYPES, formatSelectedEntitiesForPrompt } from './AIEntityMentionsInput.utils';
import { PromptInput, PromptInputSubmit, PromptInputToolbar, PromptInputTools } from './elements/PromptInput';

interface AIChatPromptInputProps {
  placeholder?: string;
  loading?: boolean;
  className?: string;
  sendMessage: (message: UIMessage) => Promise<void>;
  status: ChatStatus;
  stop: () => void;
  /** Analytics source identifier for tracking where events originate */
  source?: string;
}

const AIChatPromptInput = ({ placeholder, loading = false, className, sendMessage, status, stop, source = 'sidepanel' }: AIChatPromptInputProps) => {
  const { t } = useTranslation('ai');
  const [input, setInput] = useState('');
  const [selectedEntities, setSelectedEntities] = useState<ReturnType<typeof useMentionableEntities>['entities']>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(true);
  const { createFakeThread, threadId, newThreadId, pendingInput, clearPendingInput, refetchThreads } = useAIAssistant();
  const mentionInputRef = useRef<AIEntityMentionsInputHandle>(null);
  const pendingAutoSubmitRef = useRef(false);
  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);
  const {
    entities,
    isLoading: isLoadingMentionEntities,
    hasError: hasMentionError,
  } = useMentionableEntities({
    vehicleGroupIds,
    entityTypes: ALL_MENTIONABLE_ENTITY_TYPES,
  });

  useEffect(() => {
    if (pendingInput) {
      setInput(pendingInput);
      clearPendingInput();
      pendingAutoSubmitRef.current = true;
    }
  }, [pendingInput, clearPendingInput]);

  const { environment } = useDashboard();
  const isDev = environment?.isDev;
  const resolvedPlaceholder = placeholder ?? t('prompt_placeholder');
  const isSendingDisabled = status === 'ready' && !input.trim() && selectedEntities.length === 0;
  const activeThreadId = threadId || newThreadId;

  const scheduleThreadRefresh = useCallback(() => {
    void refetchThreads();
    setTimeout(() => {
      void refetchThreads();
    }, 2000);
  }, [refetchThreads]);

  const ensureThreadExists = useCallback(async () => {
    if (threadId) return false;

    await createFakeThread({ threadId: activeThreadId });
    return true;
  }, [activeThreadId, createFakeThread, threadId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (status !== 'ready') {
        Analytics.track('AI - Chat - Generation Stopped', { source });
        stop();
        return;
      }

      const trimmedInput = input.trim();
      const selectedEntitiesBlock = formatSelectedEntitiesForPrompt(selectedEntities);
      const composedInput = [trimmedInput, selectedEntitiesBlock].filter(Boolean).join('\n\n');

      if (!composedInput.trim()) {
        Alert.error(t('please_type_message'));
        return;
      }

      try {
        const isNewThread = await ensureThreadExists();

        Analytics.track('AI - Chat - Message Sent', { source });
        const sendMessagePromise = sendMessage({ role: 'user', id: nanoid(), parts: [{ type: 'text', text: composedInput }] });

        if (isNewThread) {
          scheduleThreadRefresh();
        }

        await sendMessagePromise;
        setInput('');
        setSelectedEntities([]);
      } catch (err) {
        setSubmitError(t('send_failed'));
        if (isDev) {
          const message = err instanceof Error ? err.message : String(err);
          // eslint-disable-next-line no-console
          console.error('[AIChatPromptInput] sendMessage failed:', message, err);
        }
      }
    },
    [ensureThreadExists, input, isDev, scheduleThreadRefresh, selectedEntities, sendMessage, source, status, stop, t]
  );

  // Auto-submit when pendingInput was consumed from the store
  useEffect(() => {
    if (pendingAutoSubmitRef.current && input.trim() && status === 'ready') {
      pendingAutoSubmitRef.current = false;
      const syntheticEvent = { preventDefault: () => undefined } as React.FormEvent;
      handleSubmit(syntheticEvent);
    }
  }, [input, handleSubmit, status]);

  if (loading) return null;

  return (
    <div data-testid='ai-chat-input' className={cn('flex w-full shrink-0 flex-col gap-1 px-4', className)}>
      <div className='relative flex flex-col gap-0.5'>
        <div
          className={cn('relative flex items-center gap-2 rounded-xl shadow-md transition-shadow', isFocused && 'ring-2 ring-brand-8')}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <PromptInput onSubmit={handleSubmit} className='relative w-full border-gray-6 bg-white'>
            <AIEntityMentionsInput
              ref={mentionInputRef}
              autoFocus
              placeholder={resolvedPlaceholder}
              value={input}
              entities={entities}
              selectedEntities={selectedEntities}
              onSelectedEntitiesChange={(nextEntities) => {
                setSelectedEntities(nextEntities);
                setSubmitError(null);
              }}
              onValueChange={(nextValue) => {
                setInput(nextValue);
                setSubmitError(null);
              }}
              isLoadingEntities={isLoadingMentionEntities}
              hasMentionError={hasMentionError}
              disabled={status !== 'ready'}
              className='pb-2'
            />
            <PromptInputToolbar className='justify-between px-3.5 pt-0 pb-2.5'>
              <PromptInputTools>
                {!MENTIONS_DISABLED && (
                  <Tooltip content={hasMentionError ? 'Menções indisponíveis no momento' : 'Adicionar menção'} asChild>
                    <div>
                      <Button
                        size='sm'
                        variant='surface'
                        color='default'
                        className='size-8 rounded-[4px] bg-gray-2/70 p-0 text-gray-10 hover:bg-gray-3'
                        disabled={status !== 'ready' || hasMentionError}
                        onClick={() => {
                          Analytics.track('AI - Chat - Mention Triggered', { source });
                          mentionInputRef.current?.insertMentionTrigger();
                        }}
                      >
                        <span className='font-semibold text-xs leading-none'>@</span>
                      </Button>
                    </div>
                  </Tooltip>
                )}
              </PromptInputTools>

              <div className='flex items-center gap-1.5'>
                <PromptInputSubmit disabled={isSendingDisabled} status={status} variant='solid' />
              </div>
            </PromptInputToolbar>
          </PromptInput>
        </div>
        {submitError && (
          <span className='text-danger-11 text-xs' role='alert'>
            {submitError}
          </span>
        )}
      </div>
      <div className='flex h-8 items-center justify-between'>
        <Feedback source='ai-chat' showTooltip={false}>
          <Typography
            variant='text-sm'
            color='secondary'
            className='flex shrink-0 cursor-pointer items-center gap-1 text-gray-9'
            onClick={() => Analytics.track('AI - Chat - Feedback Button Clicked')}
          >
            <Icon name='megaphone' className='text-gray-9' />
            Enviar sugestão?
          </Typography>
        </Feedback>
        <div className='flex items-center justify-start gap-1 text-gray-9 text-xs italic leading-4'>*Cobli IA pode cometer erros.</div>
      </div>
    </div>
  );
};

export default memo(AIChatPromptInput);
